/**
 * Colour space conversions between LAB, sRGB (D65), CMYK (naive), and hex.
 *
 * NOTE: CMYK math is naive (no ICC profile). For *visual* / designer-side
 * conversions and rough press estimates this is fine. For real press recipes,
 * the operator scans the proof with the X-Rite and saves the actual measured
 * mix into ink_mixes — that workflow doesn't use these formulas.
 */

const D65 = { X: 0.95047, Y: 1.0, Z: 1.08883 };
const EPS = 216 / 24389;
const KAPPA = 24389 / 27;

const srgbToLin = (c) =>
  c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
const linToSrgb = (c) =>
  c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;

const clamp01 = (v) => Math.max(0, Math.min(1, v));

// LAB → XYZ (D65)
const labToXyz = (L, a, b) => {
  const fy = (L + 16) / 116;
  const fx = a / 500 + fy;
  const fz = fy - b / 200;
  const fx3 = fx ** 3;
  const fz3 = fz ** 3;
  const xr = fx3 > EPS ? fx3 : (116 * fx - 16) / KAPPA;
  const yr = L > KAPPA * EPS ? ((L + 16) / 116) ** 3 : L / KAPPA;
  const zr = fz3 > EPS ? fz3 : (116 * fz - 16) / KAPPA;
  return [xr * D65.X, yr * D65.Y, zr * D65.Z];
};

const xyzToLab = (X, Y, Z) => {
  const f = (t) => (t > EPS ? Math.cbrt(t) : (KAPPA * t + 16) / 116);
  const fx = f(X / D65.X);
  const fy = f(Y / D65.Y);
  const fz = f(Z / D65.Z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
};

// XYZ ↔ sRGB linear (D65)
const xyzToRgbLin = (X, Y, Z) => [
   3.2404542 * X - 1.5371385 * Y - 0.4985314 * Z,
  -0.9692660 * X + 1.8760108 * Y + 0.0415560 * Z,
   0.0556434 * X - 0.2040259 * Y + 1.0572252 * Z,
];
const rgbLinToXyz = (r, g, b) => [
  0.4124564 * r + 0.3575761 * g + 0.1804375 * b,
  0.2126729 * r + 0.7151522 * g + 0.0721750 * b,
  0.0193339 * r + 0.1191920 * g + 0.9503041 * b,
];

export const labToRgb = (L, a, b) => {
  const [X, Y, Z] = labToXyz(L, a, b);
  const [rL, gL, bL] = xyzToRgbLin(X, Y, Z);
  return [rL, gL, bL].map((c) => Math.round(linToSrgb(clamp01(c)) * 255));
};

export const rgbToLab = (r, g, b) => {
  const [rL, gL, bL] = [r, g, b].map((c) => srgbToLin(c / 255));
  const [X, Y, Z] = rgbLinToXyz(rL, gL, bL);
  return xyzToLab(X, Y, Z);
};

export const rgbToHex = (r, g, b) =>
  '#' + [r, g, b]
    .map((c) => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();

export const hexToRgb = (hex) => {
  const cleaned = hex.replace(/^#/, '').trim();
  const expanded = cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned;
  const m = expanded.match(/^([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
};

// Naive CMYK (no ICC). Designer-side approximation only.
export const rgbToCmyk = (r, g, b) => {
  const [rn, gn, bn] = [r, g, b].map((c) => c / 255);
  const k = 1 - Math.max(rn, gn, bn);
  if (k >= 0.9999) return [0, 0, 0, 100];
  const c = (1 - rn - k) / (1 - k);
  const m = (1 - gn - k) / (1 - k);
  const y = (1 - bn - k) / (1 - k);
  return [c, m, y, k].map((v) => Math.round(v * 100));
};

export const cmykToRgb = (c, m, y, k) => {
  const [cn, mn, yn, kn] = [c, m, y, k].map((v) => v / 100);
  return [
    Math.round(255 * (1 - cn) * (1 - kn)),
    Math.round(255 * (1 - mn) * (1 - kn)),
    Math.round(255 * (1 - yn) * (1 - kn)),
  ];
};

/**
 * Naive CMYKOG estimate. No ICC, no press model — just a rough sketch.
 * Orange supplements where M+Y overlap (warm reds → orange territory).
 * Green supplements where C+Y overlap (cool yellows → green territory).
 * Real recipes come from X-Rite scan + manual mix entry.
 */
export const rgbToCmykog = (r, g, b) => {
  const [c, m, y, k] = rgbToCmyk(r, g, b);
  const o = Math.round(Math.min(m, y) * 0.3);
  const g_est = Math.round(Math.min(c, y) * 0.3);
  return [c, m, y, k, o, g_est];
};

/** True if the LAB value sits inside the sRGB display gamut. */
export const isLabInRgbGamut = (L, a, b) => {
  const [X, Y, Z] = labToXyz(L, a, b);
  const lin = xyzToRgbLin(X, Y, Z);
  return lin.every((c) => c >= -0.0001 && c <= 1.0001);
};

/**
 * Given an edit in one space, derive all four representations.
 * Returns null if input is malformed (hex parsing failure).
 */
export const deriveFromLab = (L, a, b) => {
  const rgb = labToRgb(L, a, b);
  const hex = rgbToHex(...rgb);
  const cmyk = rgbToCmyk(...rgb);
  return { lab: [L, a, b], rgb, hex, cmyk };
};

export const deriveFromCmyk = (c, m, y, k) => {
  const rgb = cmykToRgb(c, m, y, k);
  const hex = rgbToHex(...rgb);
  const lab = rgbToLab(...rgb);
  return { lab, rgb, hex, cmyk: [c, m, y, k] };
};

export const deriveFromRgb = (r, g, b) => {
  const lab = rgbToLab(r, g, b);
  const hex = rgbToHex(r, g, b);
  const cmyk = rgbToCmyk(r, g, b);
  return { lab, rgb: [r, g, b], hex, cmyk };
};

export const deriveFromHex = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return deriveFromRgb(...rgb);
};
